import { useCallback, useEffect, useState } from 'react';
import {
  getLocalStorageValue,
  setLocalStorageValue,
} from '../utils/localStorage';
import { Card } from '@yleisradio/areena-types';
import {
  CardWithPointer,
  CardWithSubcardPointer,
} from '../../store/view/types';

export const REGIONAL_PREFS_STORAGE_KEY = 'regionalChannelPrefs';

type PreferenceUpdater = (key: string, value: string) => void;
type PreferenceGetter = (
  card: CardWithPointer | CardWithSubcardPointer | null
) => Card | null;

export const useRegionalPrefs = (): [
  Record<string, string>,
  PreferenceUpdater,
  PreferenceGetter,
] => {
  const [storedValue, setStoredValue] = useState<Record<string, string>>({});

  useEffect((): void => {
    const item = getLocalStorageValue<Record<string, string>>(
      REGIONAL_PREFS_STORAGE_KEY
    );

    if (item) setStoredValue(item);
  }, []);

  const setRegionalPref: PreferenceUpdater = useCallback(
    (key: string, value: string) => {
      const update = { ...storedValue, [key]: value };

      setStoredValue(update);
      setLocalStorageValue(REGIONAL_PREFS_STORAGE_KEY, update);
    },
    [storedValue]
  );

  const getRegionalPref = useCallback(
    (key: string) => storedValue[key],
    [storedValue]
  );

  const findByPersistentKey = useCallback(
    (key: string, cards: Card[]) =>
      cards.find(
        card =>
          card?.persistentState?.key === key &&
          getRegionalPref(key) === card?.persistentState?.id
      ),
    [getRegionalPref]
  );

  const findPreferredCard = useCallback(
    (cards: Card[], key: string | undefined): Card | undefined => {
      if (!key) return undefined;

      return findByPersistentKey(key, cards);
    },
    [findByPersistentKey]
  );

  const getPreferredCard = useCallback(
    (card: CardWithPointer | CardWithSubcardPointer | null) => {
      if (!card) return null;

      if (!card.persistentState) return null;

      if (!card.cards) return null;

      const preferredCard = findPreferredCard(
        card.cards,
        card.persistentState?.key
      );

      return preferredCard || null;
    },
    [findPreferredCard]
  );

  return [storedValue, setRegionalPref, getPreferredCard];
};
